import { useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { PLATFORM_AUDIT_ACTOR_TYPES, type PlatformAuditPageDto, type PlatformAuditRowDto } from '@his/shared';
import { get, qs } from '@/lib/api';
import { dateTime, humanize } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar } from '@/components/ui/layout';
import { Button, Select } from '@/components/ui/primitives';

const ACTOR_LABELS: Record<string, string> = {
  owner: 'Owner',
  platform_user: 'Platform staff',
  tenant_user: 'Facility user',
  system: 'System',
};

function payloadSummary(payload: unknown): string {
  if (!payload || typeof payload !== 'object') return '';
  const record = payload as Record<string, unknown>;
  if (typeof record.reason === 'string') return record.reason;
  const text = JSON.stringify(payload);
  return text === '{}' ? '' : text.slice(0, 160);
}

export function OwnerAuditPage() {
  return (
    <>
      <PageHeader title="Owner audit" description="Every owner and staff action on the platform, newest first." />
      <AuditTable />
    </>
  );
}

export function AuditTable({ tenantId }: { tenantId?: string }) {
  const [action, setAction] = useState('');
  const [actorType, setActorType] = useState('');
  const audit = useInfiniteQuery({
    queryKey: ['platform-audit', tenantId ?? null, { action: action || undefined, actorType: actorType || undefined }],
    queryFn: ({ pageParam }) =>
      get<PlatformAuditPageDto>(
        `/platform/audit${qs({ tenantId, action: action || undefined, actorType: actorType || undefined, cursor: pageParam ?? undefined, limit: 50 })}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = audit.data?.pages.flatMap((page) => page.rows);

  return (
    <>
      <SearchFilterBar
        search={{ value: action, onChange: setAction, label: 'Action', placeholder: 'Action starts with…', className: 'sm:max-w-xs' }}
        filters={
          <FilterField label="Who">
            <Select className="w-48" value={actorType} onChange={(event) => setActorType(event.target.value)}>
              <option value="">Anyone</option>
              {PLATFORM_AUDIT_ACTOR_TYPES.map((type) => (
                <option key={type} value={type}>
                  {ACTOR_LABELS[type]}
                </option>
              ))}
            </Select>
          </FilterField>
        }
        onReset={
          action || actorType
            ? () => {
                setAction('');
                setActorType('');
              }
            : undefined
        }
      />
      <DataTable
        rows={rows}
        loading={audit.isLoading} refreshing={audit.isFetching}
        error={audit.error}
        rowKey={(row) => row.id}
        empty="Nothing recorded for these filters."
        columns={[
          { header: 'When', sort: (row) => row.createdAt, cell: (row) => <span className="whitespace-nowrap">{dateTime(row.createdAt)}</span> },
          { header: 'Who', sort: (row) => row.actorName ?? ACTOR_LABELS[row.actorType] ?? row.actorType, cell: (row) => row.actorName ?? ACTOR_LABELS[row.actorType] ?? row.actorType },
          { header: 'Action', sort: (row) => row.action, cell: (row) => <span className="font-mono text-xs">{row.action}</span> },
          ...(tenantId ? [] : [{ header: 'Facility', sort: (row: PlatformAuditRowDto) => row.tenant?.name, cell: (row: PlatformAuditRowDto) => row.tenant?.name ?? '—' }]),
          { header: 'On', sort: (row) => row.entityType, cell: (row) => (row.entityType ? humanize(row.entityType) : '—') },
          { header: 'Detail', cell: (row) => <span className="line-clamp-2 text-xs text-muted-foreground">{payloadSummary(row.payload)}</span> },
        ]}
        footer={
          audit.hasNextPage && (
            <Button variant="outline" size="sm" onClick={() => void audit.fetchNextPage()} loading={audit.isFetchingNextPage}>
              Older entries
            </Button>
          )
        }
      />
    </>
  );
}
